/**
 * HUD module: respawn countdown overlay + floating damage text.
 *
 * Player HP / Mana / Stamina + survival meters live on the v10 main plate
 * (hud/main-plate.ts). This file only owns the things that are NOT part of
 * the bottom plate cluster — the centered respawn overlay and the
 * world-projected floating damage layer.
 */
import * as THREE from "three";

export interface HudAPI {
  /** Show / hide the centered respawn overlay based on schema state. */
  update(state: { state: string; respawnAt: number }): void;
  /** Spawn a floating damage text at world coords. Projects to screen each frame. */
  showDamage(world: THREE.Vector3, amount: number, isCrit: boolean, attackKind: string, camera: THREE.Camera): void;
  /** Tick floating texts (re-projects world→screen for tracking). Call each frame. */
  tickDamage(camera: THREE.Camera): void;
}

export function createHud(): HudAPI {
  const overlay = document.getElementById("respawn-overlay")! as HTMLDivElement;
  const countdown = document.getElementById("respawn-countdown")! as HTMLSpanElement;
  const dmgLayer = document.getElementById("dmg-layer")! as HTMLDivElement;

  function update(s: { state: string; respawnAt: number }): void {
    if (s.state === "dead") {
      overlay.classList.remove("hidden");
      const remaining = Math.max(0, Math.ceil((s.respawnAt - Date.now()) / 1000));
      countdown.textContent = String(remaining);
    } else {
      overlay.classList.add("hidden");
    }
  }

  // Floating damage text pool (cap 32 simultaneous to prevent DOM thrash)
  type DmgEntry = { el: HTMLDivElement; world: THREE.Vector3; t0: number };
  const active: DmgEntry[] = [];
  const tmpVec = new THREE.Vector3();

  function showDamage(world: THREE.Vector3, amount: number, isCrit: boolean, attackKind: string, _camera: THREE.Camera): void {
    if (active.length >= 32) {
      const oldest = active.shift();
      if (oldest) oldest.el.remove();
    }
    const el = document.createElement("div");
    // Compose CSS class: base + crit + per-attackKind variant.
    // attackKind values:
    //   "basicAttack"     → no extra class (default yellow)
    //   "creatureBasic"   → no extra class
    //   "dot:frostbite"   → .dmg-frostbite (cyan)
    //   "dot:starving"    → .dmg-starving (red)
    //   "spell:*"         → no class yet (deferred until spells ship)
    let cls = "dmg-text";
    if (isCrit) cls += " crit";
    if (attackKind === "dot:frostbite") cls += " dmg-frostbite";
    else if (attackKind === "dot:starving") cls += " dmg-starving";
    el.className = cls;
    el.textContent = String(amount);
    dmgLayer.appendChild(el);
    active.push({ el, world: world.clone(), t0: Date.now() });
  }

  function tickDamage(camera: THREE.Camera): void {
    const now = Date.now();
    for (let i = active.length - 1; i >= 0; i--) {
      const e = active[i]!;
      if (now - e.t0 > 1100) {
        e.el.remove();
        active.splice(i, 1);
        continue;
      }
      tmpVec.copy(e.world).project(camera);
      const sx = (tmpVec.x * 0.5 + 0.5) * window.innerWidth;
      const sy = (-tmpVec.y * 0.5 + 0.5) * window.innerHeight;
      e.el.style.left = `${sx}px`;
      e.el.style.top = `${sy}px`;
    }
  }

  return { update, showDamage, tickDamage };
}
